// Icons
import { LeadStatusIcon } from '../icons/LeadStatusIcon';
import { ConfirmedIcon, LeadIcon } from '../icons/primitives';
import { CancelledIcon } from '@icons/CancelledIcon';

// Types
import type { StatusDropdownConfigMap } from '@/components/ui/status-dropdown';
import type { StatusBadgeConfigMap } from '@/components/ui/status-badge';
import { StatusActiveIcon, StatusCancelledIcon } from '@icons/StatusIcons';

// ─────────────────────────────────────────────────────────────────────────────
// Order status type
// ─────────────────────────────────────────────────────────────────────────────

export type OrderStatus = 'lead' | 'confirmed' | 'cancelled';

// ─────────────────────────────────────────────────────────────────────────────
// Dropdown config (14×14 shared status icons, matches Figma 3917:38359)
// ─────────────────────────────────────────────────────────────────────────────

export function createOrderStatusDropdownConfig(
  labels?: Record<OrderStatus, string>,
): StatusDropdownConfigMap<OrderStatus> {
  const l = labels ?? { lead: 'Lead', confirmed: 'Confirmed', cancelled: 'Cancelled' };

  return {
    lead: {
      label: l.lead,
      activeTextClassName: 'text-turmeric-yellow',
      inactiveTextClassName: 'text-turmeric-yellow',
      icon: () => <LeadIcon className="h-[15px] w-[15px] text-turmeric-yellow" />,
    },
    confirmed: {
      label: l.confirmed,
      activeTextClassName: 'text-confirmed-green',
      inactiveTextClassName: 'text-confirmed-green',
      icon: () => <ConfirmedIcon className="h-[15px] w-[15px] text-confirmed-green" />,
    },
    cancelled: {
      label: l.cancelled,
      activeTextClassName: 'text-chilli-red',
      inactiveTextClassName: 'text-chilli-red',
      icon: () => <CancelledIcon className="size-5 text-chilli-red" />,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Badge config (compact 12×12 icons for table cells)
// ─────────────────────────────────────────────────────────────────────────────

export const ORDER_STATUS_BADGE_CONFIG: StatusBadgeConfigMap<OrderStatus> = {
  confirmed: {
    label: 'Confirmed',
    icon: <StatusActiveIcon className="size-4 flex-shrink-0 text-confirmed-green" />,
    bgClassName: 'bg-[#e3fcef]',
    borderClassName: 'border-[#8cd9c3]',
    textClassName: 'text-confirmed-green',
  },
  lead: {
    label: 'Lead',
    icon: <LeadStatusIcon className="flex-shrink-0 text-turmeric-yellow" aria-hidden="true" />,
    bgClassName: 'bg-custard-yellow',
    borderClassName: 'border-butter-yellow',
    textClassName: 'text-turmeric-yellow',
  },
  cancelled: {
    label: 'Cancelled',
    icon: <StatusCancelledIcon className="size-4 flex-shrink-0 text-chilli-red" />,
    bgClassName: 'bg-[#ffeef0]',
    borderClassName: 'border-[#f5a3ad]',
    textClassName: 'text-chilli-red',
  },
};
